import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckCircle2Icon } from 'lucide-react';
const reformerTomos = [{
  num: '2',
  label: 'REFORMER I',
  title: 'Footwork & Fundamentals',
  color: 'bg-sage-600',
  borderColor: 'border-sage-600',
  desc: 'El primer mes en el Reformer completamente resuelto. Aprenderás a manejar el carro, los resortes y el footbar con precisión. Incluye la guía completa de configuración de resortes por ejercicio, 8 clases con Footwork, Short Spine, Long Spine, Hundred en Reformer, Coordination, Rowing básico y Elephant. Con cueings listos, variaciones por nivel y tabla de equivalencias Mat-Reformer para que nunca tengas que improvisar.',
  details: ['Guía completa de resortes por ejercicio y nivel', '8 clases resueltas con objetivo, ejercicios y cierre', 'Footwork completo en todas las posiciones', 'Tabla de equivalencias Mat → Reformer', 'Cueings listos para decir en clase', 'Variaciones Nivel 1, 2 y 3 por ejercicio']
}, {
  num: '4',
  label: 'REFORMER II',
  title: 'Core & Upper Body',
  color: 'bg-sage-700',
  borderColor: 'border-sage-700',
  desc: 'El segundo nivel del Reformer resuelto clase por clase. Incluye la Short Box Series completa (Round Back, Flat Back, Twist, Tree), las 5 series de Rowing (From the Hips, From the Chest, Hug, Shave, Chest Expansion), trabajo de core dinámico con correas y 8 clases con progresión real de Intermedio a Avanzado. Con guía de correas, tabla de equivalencias Core Mat vs Core Reformer y troubleshooting de los errores más frecuentes.',
  details: ['Short Box Series completa (5 ejercicios)', 'Rowing Series completa (5 series)', 'Guía de correas por ejercicio', '8 clases de Intermedio a Avanzado', 'Tabla Core Mat vs Core Reformer', 'Troubleshooting de errores frecuentes']
}, {
  num: '6',
  label: 'REFORMER III',
  title: 'Repertorio Avanzado',
  color: 'bg-sage-900',
  borderColor: 'border-sage-900',
  desc: 'El nivel más alto del sistema PILATES 360. Incluye los 4 pilares del trabajo avanzado en Reformer: inversiones controladas (Long Spine, Semi-Circle, Overhead), trabajo en plano frontal (Side Splits, Star), posiciones desafiantes (Snake, Twist, Control Front, Control Back) y Standing Series completa. Con protocolo de seguridad para ejercicios avanzados, comparativa Mat Avanzado vs Reformer Avanzado y la Clase 8 — El Gran Repertorio: la secuencia más exigente del sistema completo.',
  details: ['Inversiones completas (Long Spine, Semi-Circle, Overhead)', 'Snake, Twist, Control Front y Control Back', 'Standing Series completa en el Reformer', 'Protocolo de seguridad para ejercicios avanzados', '8 clases de Avanzado a Avanzado+', 'Clase 8 — El Gran Repertorio (75 minutos sin pausa)']
}];
function ReformerAccordion({
  tomo,
  index
}: {
  tomo: (typeof reformerTomos)[0];
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 15
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay: index * 0.08
  }} className={`rounded-xl border-l-4 ${tomo.borderColor} bg-white shadow-sm overflow-hidden`} data-id="element-274">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 md:p-5 text-left hover:bg-gray-50/50 transition-colors" data-id="element-275">
        <div className="flex items-center gap-3" data-id="element-276">
          <div className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base shadow-sm ${tomo.color}`} data-id="element-277">
            {tomo.num}
          </div>
          <div data-id="element-278">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block" data-id="element-279">
              TOMO {tomo.num} — {tomo.label}
            </span>
            <span className="text-sm md:text-base font-bold text-charcoal font-playfair" data-id="element-280">
              {tomo.title}
            </span>
          </div>
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-281">
          <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-282" />
        </motion.div>
      </button>

      <AnimatePresence data-id="element-283">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-284">
            <div className="px-4 pb-4 md:px-5 md:pb-5 border-t border-gray-100" data-id="element-285">
              <p className="text-sm text-gray-600 mt-3 mb-4 leading-relaxed" data-id="element-286">
                {tomo.desc}
              </p>
              <div className="space-y-2" data-id="element-287">
                {tomo.details.map((detail, i) => <div key={i} className="flex items-start gap-2" data-id="element-288">
                    <CheckCircle2Icon className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" data-id="element-289" />
                    <span className="text-xs md:text-sm text-gray-700" data-id="element-290">
                      {detail}
                    </span>
                  </div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function ReformerSection() {
  return <section className="py-8 md:py-14 bg-sage-50" data-id="element-291">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-292">
        <div className="text-center mb-6 md:mb-10" data-id="element-293">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-sage-100 text-sage-800 rounded-full text-[10px] md:text-xs font-bold tracking-widest uppercase mb-3 border border-sage-200" data-id="element-294">
            🏋️ Exclusivo del sistema
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-charcoal font-playfair" data-id="element-295">
            MÓDULOS EXCLUSIVOS{' '}
            <span className="text-sage" data-id="element-296">PILATES REFORMER</span>
          </h2>
          <p className="text-sm md:text-base text-gray-600 mt-2 max-w-2xl mx-auto" data-id="element-297">
            3 tomos dedicados 100% al Reformer con progresión real de
            principiante a avanzado
          </p>
        </div>

        <div className="space-y-3" data-id="element-298">
          {reformerTomos.map((tomo, index) => <ReformerAccordion key={index} tomo={tomo} index={index} data-id="element-299" />)}
        </div>
      </div>
    </section>;
}